import "@measured/puck/puck.css";
import "@measured/puck-plugin-heading-analyzer/dist/index.css";

import createEmotionCache from "@measured/puck-plugin-emotion-cache";
import { Data, Puck } from "@measured/puck";
import { Props } from "./config";
import { RootProps } from "./config/root";
import { IframeStyleInjector } from "./iframe/IframeStyleInjector";
import { criticalMuiEmotionStyles } from "./iframe/criticalMuiEmotionStyles";
import { useState, useEffect, useCallback, useRef } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { useQueryClient } from "react-query";
import { FoodsIcon } from "@foodpilot/foods";

import { useGetPuckPage, getPuckPageQueryKey } from "src/api/client-api/cms/useGetPuckPage";
import { useUpdatePuckPage } from "src/api/client-api/cms/useUpdatePuckPage";
import { useCreatePuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { useSnackbar } from "src/utils/useSnackbar";
import { Loading } from "src/components/utils/Loading";
import { rootConfig } from "./config";
import { t } from "i18next";

type PuckEditorProps = {
    contentId: string;
    onSave?: () => void;
    onPreview?: (data: Data<Props, RootProps>) => void;
};

export const PuckEditor = (props: PuckEditorProps) => {
    const { contentId, onSave, onPreview } = props;
    const [data, setData] = useState<Data<Props, RootProps> | null>(null);
    const [iframeStylesLoaded, setIframeStylesLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const currentDataRef = useRef<Data<Props, RootProps> | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    const snackbar = useSnackbar();
    const theme = useTheme();
    const createPage = useCreatePuckPage();
    const queryClient = useQueryClient();

    const { page, isFetched, isError } = useGetPuckPage({ slug: contentId });
    const { isLoading: isPageUpdateLoading, ...updatePage } = useUpdatePuckPage();

    const muiEmotionCache = createEmotionCache("css");
    const initializeData = useCallback(() => {
        const defaultData: Data<Props, RootProps> = {
            content: [],
            root: {
                props: {},
            },
        };

        if (!page && isError) {
            setData(defaultData);
            currentDataRef.current = defaultData;
            return;
        }

        if (page?.content) {
            try {
                const parsedContent = JSON.parse(page.content);

                const initialData = {
                    content: Array.isArray(parsedContent.content) ? parsedContent.content : [],
                    root: parsedContent.root || { props: {} },
                    zones: parsedContent.zones || {},
                };
                setData(initialData as Data<Props, RootProps>);
                currentDataRef.current = initialData;
            } catch (parseError) {
                console.error("Error parsing Puck content:", parseError);
                setData(defaultData);
                currentDataRef.current = defaultData;
            }
        } else {
            setData(defaultData);
            currentDataRef.current = defaultData;
        }
    }, [page, isError]);

    useEffect(() => {
        if ((isFetched && page) || isError) {
            initializeData();
        }
    }, [isFetched, page, isError, initializeData]);

    useEffect(() => {
        const preloadStyleElement = document.createElement("style");
        preloadStyleElement.id = "puck-preload-styles";
        preloadStyleElement.textContent = criticalMuiEmotionStyles;
        document.head.appendChild(preloadStyleElement);

        Array.from(document.styleSheets)
            .filter((sheet) => {
                try {
                    return sheet.cssRules !== null;
                } catch (e) {
                    return false;
                }
            })
            .forEach((sheet) => {
                try {
                    Array.from(sheet.cssRules);
                } catch (e) {
                    console.warn("Failed to access CSS rules:", e);
                }
            });

        setIframeStylesLoaded(true);

        return () => {
            const preloadElement = document.getElementById("puck-preload-styles");
            if (preloadElement) {
                document.head.removeChild(preloadElement);
            }
        };
    }, []);

    const handleDataChange = useCallback((newData: Data<Props, RootProps>) => {
        currentDataRef.current = newData;
    }, []);

    const refetchPage = useCallback(() => {
        queryClient.invalidateQueries([getPuckPageQueryKey, contentId]);
    }, [contentId, queryClient]);

    const handlePublish = useCallback(
        (newData: Data<Props, RootProps> | null) => {
            const serializedContent = JSON.stringify(newData);

            if (isError || !page) {
                createPage.mutate(
                    {
                        title: contentId,
                        content: serializedContent,
                    },
                    {
                        onSuccess: () => {
                            snackbar.forceClose();
                            snackbar.setSnackbarProps({
                                type: "success",
                                message: t("cms.puckEditor.createSuccess", "Successfully created page"),
                            });

                            refetchPage();

                            if (onSave) onSave();
                        },
                        onError: (error: Error) => {
                            console.error(`Error creating Puck page (${contentId}):`, error);
                            snackbar.forceClose();
                            snackbar.setSnackbarProps({
                                type: "error",
                                message: t("cms.puckEditor.createError", `Error: ${error.message}`),
                            });
                        },
                    },
                );
            } else {
                updatePage.mutate(
                    {
                        slug: contentId,
                        data: {
                            content: serializedContent,
                        },
                    },
                    {
                        onSuccess: () => {
                            snackbar.forceClose();
                            snackbar.setSnackbarProps({
                                type: "success",
                                message: t("cms.puckEditor.saveSuccess", "Successfully saved"),
                            });

                            refetchPage();

                            if (onSave) onSave();
                        },
                        onError: (error: Error) => {
                            console.error(`Error updating Puck page (${contentId}):`, error);
                            snackbar.forceClose();
                            snackbar.setSnackbarProps({
                                type: "error",
                                message: t("cms.puckEditor.saveError", `Error: ${error.message}`),
                            });
                        },
                    },
                );
            }
        },
        [updatePage, createPage, snackbar, onSave, contentId, isError, page, refetchPage],
    );

    const handlePreview = useCallback(() => {
        if (currentDataRef.current && onPreview) {
            onPreview(currentDataRef.current);
        }
    }, [onPreview]);

    useEffect(() => {
        setIsLoading(!isFetched || !data || !iframeStylesLoaded);
    }, [isFetched, data, iframeStylesLoaded]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                }}
            >
                <Box sx={{ mt: theme.spacing(2) }}>
                    <Loading />
                    {!isFetched ?
                        "Loading content..."
                    : !iframeStylesLoaded ?
                        "Preparing editor styles..."
                    :   "Initializing editor..."}
                </Box>
            </Box>
        );
    }

    return (
        data && (
            <Box sx={{ height: "100%", width: "100%", zIndex: 1 }}>
                <Puck
                    config={rootConfig}
                    data={data}
                    onChange={handleDataChange}
                    onPublish={handlePublish}
                    headerTitle={page?.title || ""}
                    headerPath={`${page?.slug ? `/${page?.slug}` : ""}`}
                    plugins={[muiEmotionCache]}
                    overrides={{
                        iframe: ({ children, document: iFrameDoc }) => {
                            const handleIframeRef = (node: HTMLIFrameElement | null) => {
                                if (node && !iframeRef.current) {
                                    iframeRef.current = node;
                                }
                            };

                            const handleStylesLoaded = () => {
                                setIframeStylesLoaded(true);
                            };

                            return (
                                <div ref={handleIframeRef}>
                                    <IframeStyleInjector document={iFrameDoc} onStylesLoaded={handleStylesLoaded}>
                                        {children}
                                    </IframeStyleInjector>
                                </div>
                            );
                        },
                        headerActions: () => {
                            return (
                                <Box sx={{ display: "flex", gap: theme.spacing(2) }}>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Button
                                        variant="ternary"
                                        onClick={handlePreview}
                                        startIcon={<FoodsIcon size={2} icon="edit" />}
                                    >
                                        {t("glossary.preview", "Preview")}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        disabled={isPageUpdateLoading}
                                        onClick={() => handlePublish(currentDataRef.current)}
                                        startIcon={
                                            isPageUpdateLoading ?
                                                <Loading size={14} />
                                            :   <FoodsIcon size={2} icon="save" />
                                        }
                                    >
                                        {t("glossary.save", "Save")}
                                    </Button>
                                </Box>
                            );
                        },
                    }}
                />
                <Box
                    sx={{
                        background: theme.palette.divider,
                        height: "1px",
                        width: "100%",
                    }}
                />
            </Box>
        )
    );
};
